import React, { useState, useCallback } from 'react';
import styled from 'libs/styled';
import {
    ContentSliderDesktop,
    ContentSliderStaticGrid,
    MAX_ITEMS_STATIC_GRID,
    SliderHeader,
    type ContentSliderDesktopApi,
} from 'components/sliders/ContentSlider';
import type { ButtonLinkComponent } from 'libs/sanity/types/generated';
import HighlightedContentItem from './HighlightedContentItem';
import type { HighlightedContentItemTransformed, HeadingLevel } from './types';

/** Horizontal padding so slider aligns with grid (same as --grid-padding). */
const SliderContentWrapper = styled('div')`
    padding-left: var(--grid-padding, 10px);
    padding-right: var(--grid-padding, 10px);
`;

const TRACK_EXTRA_FOR_TEXT = 70;

export interface HighlightedContentDesktopProps {
    title?: string;
    titleLevel?: HeadingLevel;
    headerSanityLink?: { sanityLink: ButtonLinkComponent; label?: string };
    items: HighlightedContentItemTransformed[];
}

const HighlightedContentDesktop: React.FC<HighlightedContentDesktopProps> = (props) => {
    const { items, title, titleLevel, headerSanityLink } = props;
    const [desktopApi, setDesktopApi] = useState<ContentSliderDesktopApi | null>(null);

    const handleSliderReady = useCallback((api: ContentSliderDesktopApi) => {
        setDesktopApi(api);
    }, []);

    if (items.length === 0) {
        return null;
    }

    const showSlider = items.length > MAX_ITEMS_STATIC_GRID;
    return (
        <>
            <SliderHeader
                title={title}
                titleLevel={titleLevel}
                sanityLink={headerSanityLink}
                navApi={showSlider ? desktopApi : undefined}
                showChevrons={showSlider && items.length > 1}
            />
            {showSlider ? (
                <SliderContentWrapper>
                    <ContentSliderDesktop<HighlightedContentItemTransformed>
                        items={items}
                        getItemKey={(item) => item.key}
                        renderItem={(item) => <HighlightedContentItem item={item} />}
                        trackHeight={(activeWidth) => activeWidth * (5 / 4) + TRACK_EXTRA_FOR_TEXT}
                        onReady={handleSliderReady}
                    />
                </SliderContentWrapper>
            ) : (
                <ContentSliderStaticGrid<HighlightedContentItemTransformed>
                    items={items}
                    getItemKey={(item) => item.key}
                    renderItem={(item) => <HighlightedContentItem item={item} />}
                />
            )}
        </>
    );
};

export default HighlightedContentDesktop;
